import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import createDOMPurify from "dompurify";
import {
  FaArrowsAlt,
  FaCrop,
  FaTimes,
} from "react-icons/fa";

import { toAbsolutePhotoUrl } from "../../../services/newsPhotoServices";

const FALLBACK = "/img/no-image.jpg";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* -------------------------------------------------------
   DOMPurify et gestion sécurisée des styles enrichis
-------------------------------------------------------- */

const purify =
  typeof window !== "undefined" ? createDOMPurify(window) : null;

const isColor = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  return (
    text === "transparent" ||
    text === "currentcolor" ||
    text === "inherit" ||
    /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(text) ||
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^[a-z]+$/i.test(text)
  );
};

const isSafeIndentSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (text === "0") return true;

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const maximumByUnit = {
    px: 160,
    rem: 10,
    em: 10,
  };

  return amount >= 0 && amount <= maximumByUnit[unit];
};

const isSafeFontSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  const match = text.match(/^(\d+(?:\.\d+)?)(px|pt|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 64 },
    pt: { min: 6, max: 48 },
    rem: { min: 0.5, max: 4 },
    em: { min: 0.5, max: 4 },
    "%": { min: 50, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const isSafeFontFamily = (value) => {
  const text = String(value || "").trim();

  if (!text || text.length > 120) return false;

  if (
    text.includes(";") ||
    text.includes("{") ||
    text.includes("}") ||
    text.includes("\\") ||
    /url\s*\(/i.test(text) ||
    /expression\s*\(/i.test(text) ||
    /var\s*\(/i.test(text)
  ) {
    return false;
  }

  return /^[-"'a-z0-9À-ÿ_,\s]+$/i.test(text);
};

const isSafeLineHeight = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (/^\d+(?:\.\d+)?$/.test(text)) {
    const amount = Number(text);

    return amount >= 0.8 && amount <= 3;
  }

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 80 },
    rem: { min: 0.8, max: 5 },
    em: { min: 0.8, max: 5 },
    "%": { min: 80, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const sanitizeInlineStyle = (styleText = "") => {
  const ALLOWED_STYLES = {
    color: (value) => isColor(value),

    "background-color": (value) => isColor(value),

    "text-align": (value) =>
      ["left", "right", "center", "justify"].includes(
        String(value).trim().toLowerCase(),
      ),

    "font-weight": (value) =>
      /^(normal|bold|bolder|lighter|[1-9]00)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-style": (value) =>
      /^(normal|italic|oblique)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-size": (value) => isSafeFontSize(value),

    "font-family": (value) => isSafeFontFamily(value),

    "line-height": (value) => isSafeLineHeight(value),

    "text-decoration": (value) =>
      /^(none|underline|line-through|overline)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "margin-left": (value) => isSafeIndentSize(value),

    "padding-left": (value) => isSafeIndentSize(value),

    "text-indent": (value) => isSafeIndentSize(value),

    "list-style-type": (value) =>
      [
        "none",
        "disc",
        "circle",
        "square",
        "decimal",
        "lower-alpha",
        "upper-alpha",
        "lower-roman",
        "upper-roman",
      ].includes(String(value).trim().toLowerCase()),
  };

  return String(styleText)
    .split(";")
    .map((rule) => rule.trim())
    .filter(Boolean)
    .map((rule) => {
      const separatorIndex = rule.indexOf(":");

      if (separatorIndex === -1) return null;

      const property = rule
        .slice(0, separatorIndex)
        .trim()
        .toLowerCase();

      const value = rule.slice(separatorIndex + 1).trim();

      return ALLOWED_STYLES[property]?.(value)
        ? `${property}: ${value}`
        : null;
    })
    .filter(Boolean)
    .join("; ");
};

if (purify) {
  purify.removeAllHooks();

  purify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName === "style") {
      const cleanStyle = sanitizeInlineStyle(data.attrValue);

      if (cleanStyle) {
        data.attrValue = cleanStyle;
      } else {
        data.keepAttr = false;
      }
    }
  });

  purify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName !== "A") return;

    const href = node.getAttribute("href") || "";

    if (/^\s*javascript:/i.test(href)) {
      node.removeAttribute("href");
    }

    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  });
}

/* -------------------------------------------------------
   Classes du contenu enrichi
-------------------------------------------------------- */

const RICH_TEXT_CLASSES = cn(
  "news-full-rich-content",
  "prose prose-slate max-w-none break-words",
  "text-[15px] leading-7 text-slate-700 sm:text-base",
  "dark:prose-invert dark:text-slate-300",

  // Paragraphes et retours à la ligne
  "prose-p:my-4",
  "[&_p]:whitespace-pre-wrap",

  // Titres
  "prose-headings:my-4",
  "prose-headings:font-barlow",
  "prose-headings:font-bold",
  "prose-headings:text-slate-950",
  "dark:prose-headings:!text-white",

  // Texte enrichi
  "prose-strong:text-slate-950",
  "prose-b:text-slate-950",
  "dark:prose-strong:!text-white",
  "dark:prose-b:!text-white",
  "prose-a:font-bold",
  "prose-a:text-sky-600",
  "hover:prose-a:underline",
  "dark:prose-a:text-sky-400",

  // Listes à puces
  "[&_ul]:!my-5",
  "[&_ul]:!list-disc",
  "[&_ul]:!pl-7",

  // Listes numérotées
  "[&_ol]:!my-5",
  "[&_ol]:!list-decimal",
  "[&_ol]:!pl-7",

  // Éléments des listes
  "[&_li]:!my-1",
  "[&_li]:!pl-1",
  "[&_li]:whitespace-pre-wrap",

  // Sous-listes à puces
  "[&_ul_ul]:!my-2",
  "[&_ul_ul]:!list-[circle]",
  "[&_ul_ul]:!pl-6",
  "[&_ul_ul_ul]:!list-[square]",

  // Sous-listes numérotées
  "[&_ol_ol]:!my-2",
  "[&_ol_ol]:!list-[lower-alpha]",
  "[&_ol_ol]:!pl-6",
  "[&_ol_ol_ol]:!list-[lower-roman]",

  // Alignements Quill
  "[&_.ql-align-center]:!text-center",
  "[&_.ql-align-right]:!text-right",
  "[&_.ql-align-justify]:!text-justify",

  // Indentations Quill
  "[&_.ql-indent-1]:!ml-6",
  "[&_.ql-indent-2]:!ml-12",
  "[&_.ql-indent-3]:!ml-16",
  "[&_.ql-indent-4]:!ml-20",
  "[&_.ql-indent-5]:!ml-24",

  // Tailles Quill
  "[&_.ql-size-small]:!text-sm",
  "[&_.ql-size-large]:!text-xl",
  "[&_.ql-size-huge]:!text-3xl",

  // Polices Quill
  "[&_.ql-font-serif]:!font-serif",
  "[&_.ql-font-monospace]:!font-mono",

  // Citations
  "prose-blockquote:my-5",
  "prose-blockquote:border-sky-500",
  "prose-blockquote:bg-sky-50",
  "prose-blockquote:px-5",
  "prose-blockquote:py-3",
  "prose-blockquote:not-italic",
  "dark:prose-blockquote:bg-sky-500/10",

  // Images intégrées au contenu
  "prose-img:mx-auto",
  "prose-img:rounded-xl",
  "prose-img:shadow-md",

  // Séparateurs et code
  "prose-hr:border-slate-200",
  "dark:prose-hr:border-white/10",
  "prose-code:break-words",

  // Suppression des marges externes
  "[&>*:first-child]:mt-0",
  "[&>*:last-child]:mb-0",
);

/* -------------------------------------------------------
   Helpers
-------------------------------------------------------- */

const stripHtml = (html = "") => {
  if (typeof document === "undefined") {
    return String(html || "").replace(/<[^>]*>/g, "").trim();
  }

  const div = document.createElement("div");
  div.innerHTML = html;

  return (div.textContent || div.innerText || "").trim();
};

const sanitizeHtml = (html = "") => {
  if (!purify) return "";

  return purify.sanitize(String(html || ""), {
    USE_PROFILES: {
      html: true,
    },
    ADD_ATTR: [
      "style",
      "target",
      "rel",
      "class",
      "data-list",
    ],
    ALLOW_DATA_ATTR: true,
    FORBID_TAGS: ["style"],
  });
};

/* -------------------------------------------------------
   Composant
-------------------------------------------------------- */

export default function NewsFullModal({ news, onClose }) {
  const [fit, setFit] = useState("cover");
  const [objPos, setObjPos] = useState("center");

  const cover = useMemo(() => {
    const imageUrl = String(news?.image_url || "").trim();

    return imageUrl
      ? toAbsolutePhotoUrl(imageUrl) || FALLBACK
      : FALLBACK;
  }, [news?.image_url]);

  const safeContent = useMemo(() => {
    const rawContent = news?.content || "";
    const plainContent = stripHtml(rawContent);

    if (!plainContent) {
      return sanitizeHtml(
        '<p style="font-style: italic;">Contenu vide.</p>',
      );
    }

    return sanitizeHtml(rawContent);
  }, [news?.content]);

  /*
   * Fermeture avec la touche Échap et blocage du scroll
   * de la page située derrière la modale.
   */
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    const previousOverflow = document.body.style.overflow;

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  const onImgError = (event) => {
    const image = event?.currentTarget;

    if (!image) return;

    if (image.dataset.fallbackApplied === "true") return;

    image.dataset.fallbackApplied = "true";
    image.src = FALLBACK;
  };

  const onImgLoad = (event) => {
    const {
      naturalWidth: width,
      naturalHeight: height,
    } = event.currentTarget;

    if (height > width) {
      setFit("contain");
      setObjPos("center 12%");
    } else {
      setFit("cover");
      setObjPos("center");
    }
  };

  const toggleFit = () => {
    setFit((currentFit) =>
      currentFit === "cover" ? "contain" : "cover",
    );
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-3 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={`Lire l’article ${
        news?.title || "Sans titre"
      }`}
      onClick={(event) => {
        if (event.currentTarget === event.target) {
          onClose?.();
        }
      }}
    >
      <div
        className={cn(
          "flex max-h-[94vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl",
          "bg-white shadow-2xl ring-1 ring-slate-200",
          "dark:bg-slate-900 dark:ring-slate-800",
        )}
        onClick={(event) => event.stopPropagation()}
      >
        {/* En-tête */}

        <div className="flex shrink-0 items-center justify-between gap-4 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 px-4 py-3 text-white">
          <h4 className="truncate font-semibold">
            {news?.title || "Sans titre"}
          </h4>

          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-full p-2 transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Image principale */}

        <div
          className="relative shrink-0 bg-slate-50 dark:bg-slate-950"
          style={{
            height: "55vh",
            minHeight: 280,
            maxHeight: 560,
          }}
        >
          <img
            src={cover}
            alt={news?.title || "Illustration"}
            className={cn(
              "absolute inset-0 h-full w-full",
              fit === "contain"
                ? "object-contain"
                : "object-cover",
            )}
            style={{ objectPosition: objPos }}
            onLoad={onImgLoad}
            onError={onImgError}
          />

          <button
            type="button"
            onClick={toggleFit}
            title={
              fit === "contain"
                ? "Recadrer l’image pour remplir la zone"
                : "Afficher l’image entièrement"
            }
            className={cn(
              "absolute bottom-3 right-3 grid h-10 w-10 place-items-center rounded-full",
              "bg-white/90 text-slate-700 shadow-lg ring-1 ring-slate-200",
              "transition hover:scale-105 hover:text-sky-600 hover:ring-sky-300",
              "dark:bg-slate-900/90 dark:text-slate-200 dark:ring-slate-700",
            )}
            aria-label={
              fit === "contain"
                ? "Recadrer l’image"
                : "Afficher toute l’image"
            }
          >
            {fit === "contain" ? (
              <FaCrop />
            ) : (
              <FaArrowsAlt />
            )}
          </button>
        </div>

        {/* Contenu scrollable */}

        <div
          className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6"
          style={{
            WebkitOverflowScrolling: "touch",
          }}
        >
          {news?.author ? (
            <p className="mb-4 text-xs text-slate-600 dark:text-slate-400">
              Par{" "}
              <span className="font-semibold">
                {news.author?.prenom} {news.author?.nom}
              </span>
            </p>
          ) : null}

          <div
            className={RICH_TEXT_CLASSES}
            style={{ tabSize: 4 }}
            dangerouslySetInnerHTML={{
              __html: safeContent,
            }}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}